import React, { useEffect, useMemo, useState } from "react";
import { useCalibratedCompass } from "../../hooks/CalibratedCompassHook";
import { getUIColorPalette } from "../../theme/ColorPalette";

/** Device information interface */
interface DeviceInfo {
    "userAgent":                    string;
    "platform":                     string;
    "hasDeviceOrientationEvent":    boolean;
    "hasDeviceMotionEvent":         boolean;
    "hasAbsoluteOrientationSensor": boolean;
    "hasOrientationPermission":     boolean;
    "hasMotionPermission":          boolean;
    "screenOrientation":            string;
    "screenAngle":                  number;
}

/** Motion data interface */
interface MotionData {
    "rotationRate":                 {
        "alpha": number | null;
        "beta":  number | null;
        "gamma": number | null;
    } | null;
    "acceleration":                 {
        "x": number | null;
        "y": number | null;
        "z": number | null;
    } | null;
    "accelerationIncludingGravity": {
        "x": number | null;
        "y": number | null;
        "z": number | null;
    } | null;
    "interval": number;
}

/** Orientation data interface */
interface OrientationData {
    "alpha":                  number | null;
    "beta":                   number | null;
    "gamma":                  number | null;
    "absolute":               boolean;
    "webkitCompassHeading"?:  number;
    "webkitCompassAccuracy"?: number;
}

/** Absolute orientation data interface */
interface AbsoluteOrientationData {
    "quaternion": [number, number, number, number];
    "timestamp":  number;
}

/**
 * Debug component for compass and gyroscope functionality
 */
interface CompassDebugProps { "isDarkUi"?: boolean }

const CompassDebug: React.FC<CompassDebugProps> = ( { isDarkUi = false } ) => {
    const compass = useCalibratedCompass();
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );
    const [ deviceInfo ] = useState<DeviceInfo>( () => {
        const DeviceOrientationEventTyped = DeviceOrientationEvent as unknown as { "requestPermission"?: () => Promise<string> };
        const DeviceMotionEventTyped = DeviceMotionEvent as unknown as { "requestPermission"?: () => Promise<string> };
        const screenTyped = screen as unknown as {
            "orientation"?: {
                "type"?:  string;
                "angle"?: number;
            };
        };

        return {
            "userAgent":                    navigator.userAgent,
            "platform":                     navigator.platform,
            "hasDeviceOrientationEvent":    "DeviceOrientationEvent" in window,
            "hasDeviceMotionEvent":         "DeviceMotionEvent" in window,
            "hasAbsoluteOrientationSensor": typeof window.AbsoluteOrientationSensor !== "undefined",
            "hasOrientationPermission":     typeof DeviceOrientationEventTyped.requestPermission === "function",
            "hasMotionPermission":          typeof DeviceMotionEventTyped.requestPermission === "function",
            "screenOrientation":            screenTyped.orientation?.type ?? "unknown",
            "screenAngle":                  screenTyped.orientation?.angle ?? 0,
        };
    } );
    const [ motionData, setMotionData ] = useState<MotionData | null>( null );
    const [ orientationData, setOrientationData ] = useState<OrientationData | null>( null );
    const [ absoluteOrientationData, setAbsoluteOrientationData ] = useState<AbsoluteOrientationData | null>( null );

    useEffect( () => {
        // Listen to motion events for debugging
        const handleMotion = ( event: DeviceMotionEvent ) => {
            setMotionData( {
                "rotationRate": event.rotationRate
                    ? {
                        "alpha": event.rotationRate.alpha,
                        "beta":  event.rotationRate.beta,
                        "gamma": event.rotationRate.gamma,
                    }
                    : null,
                "acceleration": event.acceleration
                    ? {
                        "x": event.acceleration.x,
                        "y": event.acceleration.y,
                        "z": event.acceleration.z,
                    }
                    : null,
                "accelerationIncludingGravity": event.accelerationIncludingGravity
                    ? {
                        "x": event.accelerationIncludingGravity.x,
                        "y": event.accelerationIncludingGravity.y,
                        "z": event.accelerationIncludingGravity.z,
                    }
                    : null,
                "interval": event.interval,
            } );
        };

        // Listen to orientation events for debugging
        const handleOrientation = ( event: DeviceOrientationEvent & {
            "webkitCompassHeading"?:  number;
            "webkitCompassAccuracy"?: number;
        } ) => {
            const webkitHeading = event.webkitCompassHeading;
            const webkitAccuracy = event.webkitCompassAccuracy;

            setOrientationData( {
                "alpha":                 event.alpha,
                "beta":                  event.beta,
                "gamma":                 event.gamma,
                "absolute":              event.absolute,
                "webkitCompassHeading":  typeof webkitHeading === "number" ? webkitHeading : undefined,
                "webkitCompassAccuracy": typeof webkitAccuracy === "number" ? webkitAccuracy : undefined,
            } );
        };

        // Listen to AbsoluteOrientationSensor if available
        let absoluteOrientationSensor: AbsoluteOrientationSensor | null = null;
        const handleAbsoluteOrientation = ( event: Event ) => {
            const target = event.target as AbsoluteOrientationSensor;
            setAbsoluteOrientationData( {
                "quaternion": target.quaternion,
                "timestamp":  event.timeStamp,
            } );
        };

        if ( typeof window.AbsoluteOrientationSensor !== "undefined" ) {
            try {
                absoluteOrientationSensor = new window.AbsoluteOrientationSensor( { "frequency": 60 } );
                absoluteOrientationSensor.addEventListener( "reading", handleAbsoluteOrientation );
                absoluteOrientationSensor.start();
            } catch( error ) {
                console.error( "Failed to initialize AbsoluteOrientationSensor for debug:", error );
            }
        }

        window.addEventListener( "devicemotion", handleMotion );
        window.addEventListener( "deviceorientation", handleOrientation );
        if ( "ondeviceorientationabsolute" in window ) {
            window.addEventListener( "deviceorientationabsolute", handleOrientation as EventListener );
        }

        return () => {
            window.removeEventListener( "devicemotion", handleMotion );
            window.removeEventListener( "deviceorientation", handleOrientation );
            window.removeEventListener( "deviceorientationabsolute", handleOrientation as EventListener );

            if ( absoluteOrientationSensor !== null ) {
                absoluteOrientationSensor.stop();
                absoluteOrientationSensor.removeEventListener( "reading", handleAbsoluteOrientation );
            }
        };
    }, [] );

    const requestPermissions = async() => {
        console.log( "Requesting permissions..." );
        const granted = await compass.requestPermission();
        console.log( "Permission result:", granted );
    };

    const toggleGyroscope = () => {
        compass.setGyroscopeEnabled( !compass.gyroscopeEnabled );
    };

    return (
        <div
            className="p-4 rounded-lg max-w-4xl mx-auto"
            style={{
                "backgroundColor": palette.overlayCardBackground,
                "color":           palette.overlayTextPrimary,
            }}
        >
            <h2 className="text-xl font-bold mb-4">
                🔧 Debug Boussole & Gyroscope
            </h2>
            {/* Device Information */}
            <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2">
                    📱 Informations de l&apos;appareil
                </h3>
                <div
                    className="p-3 rounded text-sm border"
                    style={{
                        "backgroundColor": palette.overlayCardBackgroundStrong,
                        "borderColor":     palette.overlayBorder,
                    }}
                >
                    <div>
                        <strong>
                            User Agent:
                        </strong>
                        {" "}
                        {deviceInfo.userAgent}
                    </div>
                    <div>
                        <strong>
                            Platform:
                        </strong>
                        {" "}
                        {deviceInfo.platform}
                    </div>
                    <div>
                        <strong>
                            DeviceOrientationEvent:
                        </strong>
                        {" "}
                        {deviceInfo.hasDeviceOrientationEvent ? "✅" : "❌"}
                    </div>
                    <div>
                        <strong>
                            DeviceMotionEvent:
                        </strong>
                        {" "}
                        {deviceInfo.hasDeviceMotionEvent ? "✅" : "❌"}
                    </div>
                    <div>
                        <strong>
                            AbsoluteOrientationSensor:
                        </strong>
                        {" "}
                        {deviceInfo.hasAbsoluteOrientationSensor ? "✅" : "❌"}
                    </div>
                    <div>
                        <strong>
                            Orientation Permission API:
                        </strong>
                        {" "}
                        {deviceInfo.hasOrientationPermission ? "✅" : "❌"}
                    </div>
                    <div>
                        <strong>
                            Motion Permission API:
                        </strong>
                        {" "}
                        {deviceInfo.hasMotionPermission ? "✅" : "❌"}
                    </div>
                    <div>
                        <strong>
                            Screen Orientation:
                        </strong>
                        {" "}
                        {deviceInfo.screenOrientation}
                    </div>
                    <div>
                        <strong>
                            Screen Angle:
                        </strong>
                        {" "}
                        {deviceInfo.screenAngle}
                        °
                    </div>
                </div>
            </div>
            {/* Compass Status */}
            <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2">
                    🧭 État de la boussole
                </h3>
                <div
                    className="p-3 rounded text-sm border"
                    style={{
                        "backgroundColor": palette.overlayCardBackgroundStrong,
                        "borderColor":     palette.overlayBorder,
                    }}
                >
                    <div>
                        <strong>
                            Cap:
                        </strong>
                        {" "}
                        {compass.heading !== null ? `${ compass.heading.toFixed( 1 ) }°` : "N/A"}
                    </div>
                    <div>
                        <strong>
                            Calibré:
                        </strong>
                        {" "}
                        {compass.calibrated ? "✅" : "❌"}
                    </div>
                    <div>
                        <strong>
                            Offset:
                        </strong>
                        {" "}
                        {compass.offset.toFixed( 1 )}
                        °
                    </div>
                    <div>
                        <strong>
                            Permission:
                        </strong>
                        {" "}
                        {compass.permission}
                    </div>
                    <div>
                        <strong>
                            Gyroscope disponible:
                        </strong>
                        {" "}
                        {compass.gyroscopeAvailable ? "✅" : "❌"}
                    </div>
                    <div>
                        <strong>
                            Gyroscope activé:
                        </strong>
                        {" "}
                        {compass.gyroscopeEnabled ? "✅" : "❌"}
                    </div>
                    <div>
                        <strong>
                            AbsoluteOrientationSensor disponible:
                        </strong>
                        {" "}
                        {compass.absoluteOrientationAvailable ? "✅" : "❌"}
                    </div>
                    <div>
                        <strong>
                            AbsoluteOrientationSensor activé:
                        </strong>
                        {" "}
                        {compass.absoluteOrientationEnabled ? "✅" : "❌"}
                    </div>
                    <div>
                        <strong>
                            Calibration manuelle:
                        </strong>
                        {" "}
                        {compass.manual ? "✅" : "❌"}
                    </div>
                    <div>
                        <strong>
                            Dernière mise à jour:
                        </strong>
                        {" "}
                        {compass.lastUpdated ? new Date( compass.lastUpdated ).toLocaleTimeString() : "Jamais"}
                    </div>
                    <div>
                        <strong>
                            Coefficient filtre:
                        </strong>
                        {" "}
                        {( compass.filterCoefficient * 100 ).toFixed( 1 )}
                        %
                    </div>
                </div>
            </div>
            {/* Controls */}
            <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2">
                    🎛️ Contrôles
                </h3>
                <div className="flex flex-wrap gap-2">
                    <button
                        className="bg-blue-500 text-white px-4 py-2 rounded text-sm"
                        onClick={requestPermissions}
                    >
                        Demander permissions
                    </button>
                    <button
                        className={`px-4 py-2 rounded text-sm ${
                            compass.gyroscopeEnabled ? "bg-red-500 text-white" : "bg-green-500 text-white"
                        }`}
                        onClick={toggleGyroscope}
                    >
                        {compass.gyroscopeEnabled ? "Désactiver" : "Activer"}
                        {" "}
                        gyroscope
                    </button>
                    {compass.absoluteOrientationAvailable &&
                      <button
                          className={`px-4 py-2 rounded text-sm ${
                              compass.absoluteOrientationEnabled ? "bg-red-500 text-white" : "bg-purple-500 text-white"
                          }`}
                          onClick={() => compass.setAbsoluteOrientationEnabled( !compass.absoluteOrientationEnabled )}
                      >
                          {compass.absoluteOrientationEnabled ? "Désactiver" : "Activer"}
                          {" "}
                          AbsoluteOrientationSensor
                      </button>}
                    <button
                        className="bg-yellow-500 text-white px-4 py-2 rounded text-sm"
                        onClick={() => compass.manualCalibrateTo( 0 )}
                    >
                        Calibrer Nord
                    </button>
                    <button
                        className="bg-gray-500 text-white px-4 py-2 rounded text-sm"
                        onClick={compass.clearManualCalibration}
                    >
                        Effacer calibration
                    </button>
                </div>
            </div>
            {/* Filter Coefficient */}
            <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2">
                    ⚙️ Coefficient du filtre complémentaire
                </h3>
                <div
                    className="p-3 rounded border"
                    style={{
                        "backgroundColor": palette.overlayCardBackgroundStrong,
                        "borderColor":     palette.overlayBorder,
                    }}
                >
                    <input
                        className="w-full mb-2"
                        max="1"
                        min="0"
                        onChange={( e ) => compass.setFilterCoefficient( parseFloat( e.target.value ) )}
                        step="0.01"
                        type="range"
                        value={compass.filterCoefficient}
                    />
                    <div
                        className="flex justify-between text-sm"
                        style={{ "color": palette.overlayTextSecondary }}
                    >
                        <span>
                            Plus gyroscope (réactif)
                        </span>
                        <span className="font-bold">
                            {( compass.filterCoefficient * 100 ).toFixed( 1 )}
                            % magnétomètre
                        </span>
                        <span>
                            Plus magnétomètre (stable)
                        </span>
                    </div>
                </div>
            </div>
            {/* Motion Data */}
            {motionData &&
              <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-2">
                      📊 Données du gyroscope
                  </h3>
                  <div
                      className="p-3 rounded text-sm border"
                      style={{
                          "backgroundColor": palette.overlayCardBackgroundStrong,
                          "borderColor":     palette.overlayBorder,
                      }}
                  >
                      <div className="grid grid-cols-2 gap-4">
                          <div>
                              <div className="font-semibold mb-2">
                                  🔄 Vitesse de rotation (°/s)
                              </div>
                              <div className="space-y-1">
                                  <div className="flex justify-between">
                                      <span>
                                          Alpha (Z):
                                      </span>
                                      <span className={motionData.rotationRate?.alpha ? "text-blue-600 font-mono" : "text-gray-400"}>
                                          {motionData.rotationRate?.alpha?.toFixed( 3 ) || "N/A"}
                                      </span>
                                  </div>
                                  <div className="flex justify-between">
                                      <span>
                                          Beta (X):
                                      </span>
                                      <span className={motionData.rotationRate?.beta ? "text-blue-600 font-mono" : "text-gray-400"}>
                                          {motionData.rotationRate?.beta?.toFixed( 3 ) || "N/A"}
                                      </span>
                                  </div>
                                  <div className="flex justify-between">
                                      <span>
                                          Gamma (Y):
                                      </span>
                                      <span className={motionData.rotationRate?.gamma ? "text-blue-600 font-mono" : "text-gray-400"}>
                                          {motionData.rotationRate?.gamma?.toFixed( 3 ) || "N/A"}
                                      </span>
                                  </div>
                              </div>
                          </div>
                          <div>
                              <div className="font-semibold mb-2">
                                  ⚡ Accélération (m/s²)
                              </div>
                              <div className="space-y-1">
                                  <div className="flex justify-between">
                                      <span>
                                          X:
                                      </span>
                                      <span className={motionData.acceleration?.x ? "text-green-600 font-mono" : "text-gray-400"}>
                                          {motionData.acceleration?.x?.toFixed( 3 ) || "N/A"}
                                      </span>
                                  </div>
                                  <div className="flex justify-between">
                                      <span>
                                          Y:
                                      </span>
                                      <span className={motionData.acceleration?.y ? "text-green-600 font-mono" : "text-gray-400"}>
                                          {motionData.acceleration?.y?.toFixed( 3 ) || "N/A"}
                                      </span>
                                  </div>
                                  <div className="flex justify-between">
                                      <span>
                                          Z:
                                      </span>
                                      <span className={motionData.acceleration?.z ? "text-green-600 font-mono" : "text-gray-400"}>
                                          {motionData.acceleration?.z?.toFixed( 3 ) || "N/A"}
                                      </span>
                                  </div>
                              </div>
                          </div>
                      </div>
                      <div className="mt-4">
                          <div className="font-semibold mb-2">
                              🌍 Accélération avec gravité (m/s²)
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-xs">
                              <div className="text-center">
                                  <div className="font-semibold">
                                      X
                                  </div>
                                  <div className={motionData.accelerationIncludingGravity?.x ? "text-purple-600 font-mono" : "text-gray-400"}>
                                      {motionData.accelerationIncludingGravity?.x?.toFixed( 3 ) || "N/A"}
                                  </div>
                              </div>
                              <div className="text-center">
                                  <div className="font-semibold">
                                      Y
                                  </div>
                                  <div className={motionData.accelerationIncludingGravity?.y ? "text-purple-600 font-mono" : "text-gray-400"}>
                                      {motionData.accelerationIncludingGravity?.y?.toFixed( 3 ) || "N/A"}
                                  </div>
                              </div>
                              <div className="text-center">
                                  <div className="font-semibold">
                                      Z
                                  </div>
                                  <div className={motionData.accelerationIncludingGravity?.z ? "text-purple-600 font-mono" : "text-gray-400"}>
                                      {motionData.accelerationIncludingGravity?.z?.toFixed( 3 ) || "N/A"}
                                  </div>
                              </div>
                          </div>
                      </div>
                      <div
                          className="mt-3 text-xs"
                          style={{ "color": palette.overlayTextSecondary }}
                      >
                          Intervalle:
                          {" "}
                          {motionData.interval || "N/A"}
                          {" "}
                          ms
                      </div>
                  </div>
              </div>}
            {/* Orientation Data */}
            {orientationData &&
              <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-2">
                      🧭 Données du magnétomètre
                  </h3>
                  <div
                      className="p-3 rounded text-sm border"
                      style={{
                          "backgroundColor": palette.overlayCardBackgroundStrong,
                          "borderColor":     palette.overlayBorder,
                      }}
                  >
                      <div className="grid grid-cols-2 gap-4">
                          <div>
                              <div className="font-semibold mb-2">
                                  🧲 Orientation (degrés)
                              </div>
                              <div className="space-y-1">
                                  <div className="flex justify-between">
                                      <span>
                                          Alpha (Z):
                                      </span>
                                      <span className={orientationData.alpha ? "text-red-600 font-mono" : "text-gray-400"}>
                                          {orientationData.alpha?.toFixed( 1 ) || "N/A"}
                                          °
                                      </span>
                                  </div>
                                  <div className="flex justify-between">
                                      <span>
                                          Beta (X):
                                      </span>
                                      <span className={orientationData.beta ? "text-red-600 font-mono" : "text-gray-400"}>
                                          {orientationData.beta?.toFixed( 1 ) || "N/A"}
                                          °
                                      </span>
                                  </div>
                                  <div className="flex justify-between">
                                      <span>
                                          Gamma (Y):
                                      </span>
                                      <span className={orientationData.gamma ? "text-red-600 font-mono" : "text-gray-400"}>
                                          {orientationData.gamma?.toFixed( 1 ) || "N/A"}
                                          °
                                      </span>
                                  </div>
                              </div>
                          </div>
                          <div>
                              <div className="font-semibold mb-2">
                                  📊 Métadonnées
                              </div>
                              <div className="space-y-1">
                                  <div className="flex justify-between">
                                      <span>
                                          Absolute:
                                      </span>
                                      <span className={orientationData.absolute ? "text-green-600" : "text-orange-600"}>
                                          {orientationData.absolute ? "✅" : "❌"}
                                      </span>
                                  </div>
                                  <div className="flex justify-between">
                                      <span>
                                          WebKit Cap:
                                      </span>
                                      <span className={orientationData.webkitCompassHeading ? "text-blue-600 font-mono" : "text-gray-400"}>
                                          {orientationData.webkitCompassHeading?.toFixed( 1 ) || "N/A"}
                                          °
                                      </span>
                                  </div>
                                  <div className="flex justify-between">
                                      <span>
                                          Précision:
                                      </span>
                                      <span className={orientationData.webkitCompassAccuracy ? "text-purple-600" : "text-gray-400"}>
                                          {orientationData.webkitCompassAccuracy || "N/A"}
                                      </span>
                                  </div>
                              </div>
                          </div>
                      </div>
                      <div
                          className="mt-4 p-2 rounded text-xs"
                          style={{ "backgroundColor": palette.overlayCardBackgroundSoft }}
                      >
                          <div className="font-semibold mb-1">
                              ℹ️ Explication des axes :
                          </div>
                          <div>
                              •
                              {" "}
                              <strong>
                                  Alpha (Z)
                              </strong>
                              {" "}
                              : Rotation autour de l&apos;axe vertical (cap de la boussole)
                          </div>
                          <div>
                              •
                              {" "}
                              <strong>
                                  Beta (X)
                              </strong>
                              {" "}
                              : Inclinaison avant/arrière
                          </div>
                          <div>
                              •
                              {" "}
                              <strong>
                                  Gamma (Y)
                              </strong>
                              {" "}
                              : Inclinaison gauche/droite
                          </div>
                      </div>
                  </div>
              </div>}
            {/* AbsoluteOrientationSensor Data */}
            {absoluteOrientationData &&
              <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-2">
                      🎯 Données AbsoluteOrientationSensor
                  </h3>
                  <div
                      className="p-3 rounded text-sm border"
                      style={{
                          "backgroundColor": palette.overlayCardBackgroundStrong,
                          "borderColor":     palette.overlayBorder,
                      }}
                  >
                      <div className="grid grid-cols-2 gap-4">
                          <div>
                              <div className="font-semibold mb-2">
                                  🔢 Quaternion [x, y, z, w]
                              </div>
                              <div className="space-y-1">
                                  <div className="flex justify-between">
                                      <span>
                                          X:
                                      </span>
                                      <span className="text-indigo-600 font-mono">
                                          {absoluteOrientationData.quaternion?.[0]?.toFixed( 4 ) || "N/A"}
                                      </span>
                                  </div>
                                  <div className="flex justify-between">
                                      <span>
                                          Y:
                                      </span>
                                      <span className="text-indigo-600 font-mono">
                                          {absoluteOrientationData.quaternion?.[1]?.toFixed( 4 ) || "N/A"}
                                      </span>
                                  </div>
                                  <div className="flex justify-between">
                                      <span>
                                          Z:
                                      </span>
                                      <span className="text-indigo-600 font-mono">
                                          {absoluteOrientationData.quaternion?.[2]?.toFixed( 4 ) || "N/A"}
                                      </span>
                                  </div>
                                  <div className="flex justify-between">
                                      <span>
                                          W:
                                      </span>
                                      <span className="text-indigo-600 font-mono">
                                          {absoluteOrientationData.quaternion?.[3]?.toFixed( 4 ) || "N/A"}
                                      </span>
                                  </div>
                              </div>
                          </div>
                          <div>
                              <div className="font-semibold mb-2">
                                  📐 Calculs dérivés
                              </div>
                              <div className="space-y-1 text-xs">
                                  <div className="flex justify-between">
                                      <span>
                                          Timestamp:
                                      </span>
                                      <span
                                          className="font-mono"
                                          style={{ "color": palette.overlayTextSecondary }}
                                      >
                                          {absoluteOrientationData.timestamp ? new Date( absoluteOrientationData.timestamp ).toLocaleTimeString() : "N/A"}
                                      </span>
                                  </div>
                                  <div className="flex justify-between">
                                      <span>
                                          Magnitude:
                                      </span>
                                      <span
                                          className="font-mono"
                                          style={{ "color": palette.overlayTextSecondary }}
                                      >
                                          {absoluteOrientationData.quaternion !== null && absoluteOrientationData.quaternion !== undefined
                                              ? Math.sqrt( absoluteOrientationData.quaternion[0] ** 2 +
                                                absoluteOrientationData.quaternion[1] ** 2 +
                                                absoluteOrientationData.quaternion[2] ** 2 +
                                                absoluteOrientationData.quaternion[3] ** 2 ).toFixed( 4 )
                                              : "N/A"}
                                      </span>
                                  </div>
                              </div>
                          </div>
                      </div>
                      <div
                          className="mt-4 p-2 rounded text-xs"
                          style={{ "backgroundColor": palette.overlayCardBackgroundSoft }}
                      >
                          <div className="font-semibold mb-1">
                              ℹ️ Quaternion :
                          </div>
                          <div>
                              • Représentation mathématique de l&apos;orientation 3D
                          </div>
                          <div>
                              • Plus stable que les angles d&apos;Euler
                          </div>
                          <div>
                              • Utilisé par l&apos;AbsoluteOrientationSensor pour la précision
                          </div>
                      </div>
                  </div>
              </div>}
            {/* Instructions */}
            <div
                className="p-3 rounded text-sm border"
                style={{
                    "backgroundColor": palette.overlayCardBackgroundSoft,
                    "borderColor":     palette.overlayBorder,
                }}
            >
                <h4 className="font-semibold mb-2">
                    📋 Instructions de test:
                </h4>
                <ol className="list-decimal list-inside space-y-1">
                    <li>
                        Cliquez sur &quot;Demander permissions&quot; pour autoriser les capteurs
                    </li>
                    <li>
                        Activez le gyroscope si disponible
                    </li>
                    <li>
                        Tournez votre appareil pour voir les données changer
                    </li>
                    <li>
                        Testez différentes orientations (portrait, paysage, incliné)
                    </li>
                    <li>
                        Vérifiez que les données du gyroscope apparaissent
                    </li>
                </ol>
            </div>
        </div>
    );
};

export default CompassDebug;
